import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref, onMounted, onUnmounted, watch } from 'vue'
import { offset, arrow, UseFloatingOptions } from '@floating-ui/vue'

import LButton from '@/components/core/Button.vue'
import FloatingArrow from '@/components/core/popover/FloatingArrow.vue'
import { usePopover, useDismiss, unwrapElement } from '@/composables/popover'
import { productTourInjectionKey } from '@/composables/productTour'

interface ProductTourStepProps {
  /**
   * Unique step identifier
   */
  stepId: string
  /**
   * Step order in the tour
   */
  order: number
  /**
   * Popover placement
   */
  placement?: UseFloatingOptions['placement']
  /**
   * Additional offset from reference element in pixels
   */
  offset?: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductTourStep',
  props: {
    stepId: null,
    order: null,
    placement: { default: 'bottom-start' },
    offset: { default: 8 }
  },
  setup(__props: any) {

const props = __props as { stepId: string, order: number, placement: UseFloatingOptions['placement'], offset: number };



// Inject tour state from ProductTour component
const tour = inject(productTourInjectionKey)
if (!tour) {
  throw new Error(
    'Unknown product tour context, did you forget to wrap <ProductTour> around this component?',
  )
}

// Register this step with the tour
onMounted(() => {
  let element: Element | undefined

  if (referenceRef.value) {
    const unwrappedElement = unwrapElement(referenceRef.value)
    if (unwrappedElement && unwrappedElement instanceof Element) {
      element = unwrappedElement
    }
  }

  tour.registerStep({
    id: props.stepId,
    order: props.order,
    element,
  })
})

onUnmounted(() => {
  tour.unregisterStep(props.stepId)
})

// Calculate if this step should be shown
const shouldShowStep = computed(() => {
  return tour.isStepActive(props.stepId)
})

const arrowRef = ref<HTMLElement>()

// Calculate floating offset based on arrow size
const arrowSize = 8 // 8px arrow
const floatingOffset = Math.sqrt(2 * arrowSize ** 2) / 2 + props.offset

const { referenceRef, floatingRef, floatingStyles, middlewareData, placement } =
  usePopover({
    placement: props.placement,
    isOpen: shouldShowStep,
    middleware: [offset(floatingOffset), arrow({ element: arrowRef })],
  })

// Update element reference when it changes
watch(
  referenceRef,
  (newRef) => {
    if (newRef) {
      const element = unwrapElement(newRef)
      if (element && element instanceof Element) {
        tour.updateStepElement(props.stepId, element)
      }
    }
  },
  { immediate: true },
)

useDismiss({ referenceRef, floatingRef, isOpen: shouldShowStep })

// Navigation handlers
function handleNext() {
  tour?.nextStep()
}

function handlePrevious() {
  tour?.previousStep()
}

function handleSkip() {
  tour?.skipTour()
}

return { __sfc: true,props, tour, shouldShowStep, arrowRef, arrowSize, floatingOffset, referenceRef, floatingRef, floatingStyles, middlewareData, placement, handleNext, handlePrevious, handleSkip, LButton, FloatingArrow }
}

})